import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import ChevronDown from "../../../assets/ChevronDown.svg";
import Close from "../../../assets/Close.svg";
import CheckMark from "../../../assets/CurrencyCheckMark.svg";
import { useNavigate, useParams } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import Button from "shared/ui/button";
import { OperationType, OrderType } from "static/types";
import { dashboardStore } from "entities/dashboard";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import {
  getCommission,
  getMLValue,
  getPreviewIccContractUrl,
  timer,
} from "helpers/functions";
import { formatMoney } from "helpers/money";
import Loading from "widgets/loading";
import Modal from "widgets/auth/modal";
import { useTranslation } from "react-i18next";
import { ResponseCodes } from "shared/api/sircap";
import { toast } from "react-toastify";
import TermsCheckbox from "pages/onboarding/components/terms-checkbox";
import useCanAction from "hooks/useCanAction";
import { sessionModel } from "entities/session";
import Currency from "../components/deposit-currency";
import { CurrencyModal } from "../components/currency-modal";
import { GetCurrByCode } from "helpers/currencies";
import { CURRENCY_FLAGS } from "static/currencies";
import moment from "moment";
import TopUp from "../../../assets/TopUpLiner.svg";
import { ChartViewCustom } from "widgets/market/chart-view";
import { useLocalizedImage } from "hooks/useLocalizedImage";
import UATEBannerAM from "../../../assets/strategy/uate/UATEBannerAM.png";
import UATEBannerRU from "../../../assets/strategy/uate/UATEBannerRU.png";
import UATEBannerEN from "../../../assets/strategy/uate/UATEBannerEN.png";
import {
  LazyDocViewer,
  LazyPDFRenderer,
  useLazyIsMobile,
} from "utils/lazy-libs";
// @ts-ignore
import DocumentEN from "./documents/UATE_CN_TERMS_EN.pdf";
// @ts-ignore
import DocumentAM from "./documents/UATE_CN_TERMS_AM.pdf";
import DocumentDownloadIcon from "../../../assets/CloudDownload.png";
import BargainingAlert from "../components/alert";

type PDFProps = {
  uri: string;
};

const PDF: React.FC<PDFProps> = React.memo(
  ({ uri }) => {
    const { isMobile } = useLazyIsMobile();

    return (
      <LazyDocViewer
        className="doc-viewer"
        documents={[{ uri, fileType: "pdf" }]}
        pluginRenderers={[LazyPDFRenderer]}
        config={{
          header: {
            disableHeader: true,
            disableFileName: true,
          },
          pdfZoom: {
            defaultZoom: isMobile ? 1 : 2,
            zoomJump: 0.2,
          },
          pdfVerticalScrollByDefault: true,
          loadingRenderer: { overrideComponent: () => <Loading /> },
        }}
      />
    );
  },
  (prev, next) => {
    return prev.uri === next.uri;
  },
);

const OrderTypes = {
  [OperationType.Buy]: OrderType.Market,
  [OperationType.Sell]: OrderType.Market,
};
const AgentStrategies = [
  "AI_INDEX",
  "AI_INDEX_4",
  "IPO_INDEX",
  "BLOCKCHAIN_INDEX",
  "ZAMANIA_CN",
];
const AgentOptions = [
  "INDEX_OPTION",
  "AI_INDEX_4_OPTION",
  "BLOCKCHAIN_INDEX_OPTION",
];

export const InvestPage = () => {
  const [checkedContract, setCheckedContract] = useState(false);
  const [isProtected, setIsProtected] = useState(false);
  const navigate = useNavigate();
  const [amount, setAmount] = useState<number>(0);
  const { asset_id, operation } = useParams();
  const [confirmDeal, setConfirmDeal] = useState(false);
  const { user_deposit_accounts, user_brokerage_accounts, loadUserAccounts } =
    useStore(dashboardStore);
  const { isLoading, assetInfo, loadAssetInfo, openOrder } =
    useStore(marketStore);
  const { t: tError } = useTranslation("errors");
  const { i18n, t } = useTranslation("app");
  const { t: i } = useTranslation("investingWithoutLoss");
  const { t: u } = useTranslation("union");
  const [load, setLoad] = useState(false);
  const [dealError, setDealError] = useState(false);
  const action = useCanAction();
  const [selectedIndex, setSelectedIndex] = useState("IPO_INDEX");

  const [indexModal, setIndexModal] = useState<null | "custom">(null);
  const [checked, setChecked] = useState<boolean[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [accountsModal, setAccountsModal] = useState(false);

  const { user, entity } = useStore(sessionModel.sessionStore);

  const symbol = assetInfo?.asset?.symbol || "";
  const baseCurrency = assetInfo?.asset?.currency?.code || "";
  const currSymbol = GetCurrByCode(selectedCurrency)?.symbol || "";

  const asset = user_brokerage_accounts?.find(
    (ua) => ua.asset.symbol === symbol,
  );
  const indexList =
    user_brokerage_accounts?.filter(
      (ua) =>
        AgentStrategies.includes(ua.asset.symbol) &&
        ua.asset.data.capital_protection,
    ) || [];
  const selectedIndexAsset = user_brokerage_accounts?.find(
    (ua) => ua.asset.symbol === selectedIndex,
  );
  const isOptionIndex = AgentOptions.includes(assetInfo?.asset?.symbol || "");
  const capitalProtection =
    isOptionIndex && operation === OperationType.Buy
      ? selectedIndexAsset?.asset?.data?.capital_protection
      : assetInfo?.asset?.data?.capital_protection;
  const capitalProtectionCommission =
    assetInfo?.asset?.data?.capital_protection_commission;
  const contractType = assetInfo?.customInfo?.info?.contract_type;
  const contractAmountType = assetInfo?.customInfo?.info?.contract_amount_type;
  const canTradeFromDate =
    assetInfo?.customInfo?.info?.can_trade_from_date || null;
  const canTrade = canTradeFromDate
    ? moment(canTradeFromDate, "YYYY-MM-DD").isSameOrBefore(moment())
    : true;
  const isIpo = assetInfo?.customInfo?.info?.is_ipo || false;
  const [step, setStep] = useState<number>(assetInfo?.step || 1);
  const currencies = assetInfo?.customInfo?.info?.currencies || [];
  const alertText =
    currencies.find((c) => c.code === selectedCurrency)?.alert_text || null;
  const showOtherAccounts = currencies.length > 0;

  useEffect(() => {
    const baseStep = assetInfo?.step || 1;
    if (selectedCurrency) {
      const currItem = currencies?.find((c) => c.code === selectedCurrency);
      setStep(currItem?.step || baseStep || 1);
    } else {
      setStep(baseStep || 1);
    }
  }, [assetInfo?.step, selectedCurrency, currencies]);

  useEffect(() => {
    setSelectedCurrency(baseCurrency);
  }, [baseCurrency]);

  const userAccount = useMemo(() => {
    return user_deposit_accounts.find(
      (acc) => acc.currency.code === selectedCurrency,
    );
  }, [user_deposit_accounts, selectedCurrency]);

  const agreements = useMemo(
    () => assetInfo?.customInfo?.info?.agreements || [],
    [assetInfo],
  );

  useEffect(() => {
    setChecked(agreements.map(() => false));
  }, [agreements]);

  useEffect(() => {
    try {
      loadAssetInfo(asset_id);
      loadUserAccounts();
    } catch (error) {
      navigate(-1);
    }
  }, []);

  const getUserAccount = useCallback(() => {
    const curr = selectedCurrency;
    const acc = userAccount;
    return {
      symbol: curr,
      amount: formatMoney(acc?.amount || 0),
    };
  }, [userAccount, selectedCurrency]);

  const getAssetPriceAsk = useCallback(() => {
    try {
      const info = assetInfo?.asset;
      if (info) {
        const { ai, nominal } = info?.data;
        return formatMoney((info.ask / 100) * nominal + ai);
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }, [assetInfo]);

  const getAssetPriceBid = useCallback(() => {
    try {
      const info = assetInfo?.asset;
      if (info) {
        const { ai, nominal } = info?.data;
        return formatMoney((info.bid / 100) * nominal + ai);
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }, [assetInfo]);

  const getAssetPrice = useCallback(() => {
    if (operation === OperationType.Buy) {
      return getAssetPriceAsk();
    } else if (operation === OperationType.Sell) {
      return getAssetPriceBid();
    } else {
      return 0;
    }
  }, [assetInfo, operation]);

  const getTotalQuantity = useCallback((): number => {
    try {
      const am = selectedCurrency === "RUB" ? amount / 84 : amount;
      const price = getAssetPrice();
      const q = am / price;
      if (!q) return 0;
      return isIpo ? Math.floor(q) : parseFloat(q.toFixed(6));
    } catch (error) {
      return 0;
    }
  }, [amount, getAssetPrice, isIpo, selectedCurrency]);

  const getAvailableLots = useCallback(() => {
    try {
      const price = getAssetPrice();
      const balance = getUserAccount().amount;
      const [_, __, commision] = getCommission(
        assetInfo?.commission || 0,
        balance,
        OperationType.Buy,
        assetInfo?.minimal_commission || 0,
        false,
        0,
        assetInfo?.min_commission_currency,
        assetInfo?.asset?.currency?.code,
        assetInfo?.min_commission_cb_rate,
      );
      const lots = Math.floor((balance - commision) / price);
      if (lots === Infinity || lots < 0) return 0;
      return lots;
    } catch (error) {
      return 0;
    }
  }, [assetInfo, getUserAccount]);

  const getTotalAmount = () => {
    try {
      return +(asset!.quantity * asset!.price).toFixed(2);
    } catch (error) {
      return 0;
    }
  };

  const getAmountRange = useCallback(() => {
    try {
      let minAmount = 0;
      let maxAmount = 0;
      if (operation === OperationType.Buy) {
        const price = getAssetPrice();
        let minDeposit = 0;
        let maxDeposit = 0;
        if (selectedCurrency !== baseCurrency) {
          const currItem = currencies?.find((c) => c.code === selectedCurrency);
          minDeposit = currItem?.min_deposit || 0;
          maxDeposit = currItem?.max_deposit || 0;
        } else {
          minDeposit = assetInfo?.customInfo?.info?.min_deposit || 0;
          maxDeposit = assetInfo?.customInfo?.info?.max_deposit || 0;
        }

        minAmount = minDeposit;
        maxAmount = +(getAvailableLots() * price).toFixed(4);
        if (maxDeposit) {
          maxAmount = maxDeposit;
          // maxAmount = maxAmount > maxDeposit ? maxDeposit : maxAmount;
        }
      } else {
        maxAmount = getTotalAmount();
      }

      // Ensure we never return NaN
      if (isNaN(minAmount)) minAmount = 0;
      if (isNaN(maxAmount)) maxAmount = 0;

      return [minAmount, maxAmount];
    } catch (error) {
      return [0, 0];
    }
  }, [
    assetInfo,
    getAvailableLots,
    getAssetPrice,
    getTotalAmount,
    baseCurrency,
    selectedCurrency,
    currencies,
    operation,
  ]);

  const getCommissionWrap = useCallback(() => {
    try {
      let addCommission = 0;
      try {
        if (isOptionIndex && operation === OperationType.Buy) {
          return getCommission(
            assetInfo?.commission || 0,
            (selectedIndexAsset?.quantity || 0) *
              (selectedIndexAsset?.price || 0),
            operation!,
            assetInfo?.asset?.minimal_commission || 0,
            false,
            0,
            assetInfo?.min_commission_currency,
            assetInfo?.asset?.currency?.code,
            assetInfo?.min_commission_cb_rate,
          );
        } else if (isProtected && capitalProtection) {
          // estimate capital protection amount commission
          const commission = (amount / 100) * capitalProtectionCommission;
          if (commission) {
            addCommission = +commission.toFixed(2);
          }
        }
      } catch (error) {
        console.log(error);
      }

      return getCommission(
        assetInfo?.commission || 0,
        amount!,
        operation!,
        assetInfo?.asset?.minimal_commission || 0,
        false,
        addCommission,
        assetInfo?.min_commission_currency,
        assetInfo?.asset?.currency?.code,
        assetInfo?.min_commission_cb_rate,
      );
    } catch (error) {
      return [0, 0, 0];
    }
  }, [
    assetInfo,
    amount,
    isProtected,
    capitalProtection,
    capitalProtectionCommission,
    isOptionIndex,
  ]);

  const getTotalPriceAndCommission = (): string => {
    try {
      const total = amount;
      if (!total) return "";
      const [_, additional, comm] = getCommissionWrap();
      const commission = comm + additional;
      const protection = amount / 10;

      return `${+(total + commission + (isProtected ? protection : 0)).toFixed(
        2,
      )}${currSymbol}`;
    } catch (error) {
      return "";
    }
  };

  const getAssetCommission = () => {
    try {
      if (!amount) return <></>;
      const [commission, additional, commAmount] = getCommissionWrap();

      const minCurrency = assetInfo?.min_commission_currency;
      const assetCurrencyCode = assetInfo?.asset?.currency?.code;
      const isCrossCurrencyMin = !!minCurrency && !!assetCurrencyCode && minCurrency !== assetCurrencyCode;

      if (isCrossCurrencyMin) {
        const cbRate = assetInfo?.min_commission_cb_rate ?? null;
        const minInMin = assetInfo!.minimal_commission;
        const finalInAsset = Math.abs(commAmount);
        const finalInMin = cbRate ? +(finalInAsset * cbRate).toFixed(2) : minInMin;
        if (!finalInAsset && !finalInMin) return <></>;
        const percentApplied = finalInMin > minInMin + 0.01;
        const rateDisplay =
          cbRate && cbRate >= 1
            ? `1 ${assetCurrencyCode} = ${cbRate} ${minCurrency}`
            : cbRate
              ? `1 ${minCurrency} = ${+(1 / cbRate).toFixed(4)} ${assetCurrencyCode}`
              : null;
        return (
          <p style={{ width: "100%", textAlign: "left" }}>
            {t("commission")}:{" "}
            <b>{finalInAsset} {assetCurrencyCode}</b>
            {rateDisplay && (
              <span style={{ opacity: 0.65, fontSize: "0.85em" }}>
                {" "}({percentApplied ? `${commission}%, ` : ""}{finalInMin} {minCurrency}, {rateDisplay})
              </span>
            )}
          </p>
        );
      }

      if (!commAmount) return <></>;

      return (
        <p style={{ width: "100%", textAlign: "left" }}>
          {t("commission")}:{" "}
          {commAmount > assetInfo!.minimal_commission ? (
            <>
              {commission}% {additional ? `+ ${additional}$` : ""}
            </>
          ) : (
            <>{additional ? `+ ${additional}$` : ""}</>
          )}
          <b>
            ({commAmount}
            {currSymbol})
          </b>
        </p>
      );
    } catch (error) {
      return <></>;
    }
  };

  useEffect(() => {
    if (
      assetInfo?.asset?.symbol === "INDEX_OPTION" &&
      operation === OperationType.Buy &&
      selectedIndexAsset
    ) {
      if (isProtected) {
        const capProtection =
          selectedIndexAsset.asset.data?.capital_protection || 0;
        setAmount(
          ((selectedIndexAsset.quantity * selectedIndexAsset.price) / 100) *
            capProtection,
        );
      } else {
        setAmount(0);
      }
    }
  }, [assetInfo, selectedIndexAsset, isProtected]);

  const getIndexInvestments = () => {
    const indexAsset = user_brokerage_accounts.find(
      (v) => v.asset.symbol === selectedIndex,
    );
    if (indexAsset) {
      return indexAsset.quantity * indexAsset.price;
    } else {
      return 0;
    }
  };

  const handleBuy = (pinCode: string) => {
    if (assetInfo?.asset && operation) {
      setLoad(true);
      openOrder(
        assetInfo.asset.id,
        {
          pinCode,
          comments: "",
          asset: assetInfo.asset.id,
          type: OrderTypes[operation as OperationType],
          price: getAssetPrice(),
          quantity: getTotalQuantity(),
          operation: operation!,
          with_sirius: false,
          use_capital_protection: capitalProtection && isProtected,
          custom_commission: isOptionIndex ? getCommissionWrap()[2] : undefined,
          specific_amount: amount || undefined,
          specific_currency: selectedCurrency || undefined,
        },
        tError,
        (error?: number) => {
          setLoad(false);
          if (error) {
            if (error === ResponseCodes.WrongPinCode) {
              setDealError(true);
            } else {
              const err = String(error);
              toast.error(tError(err));
            }
          } else {
            if (symbol === "POST_IPO") {
              toast.success(
                "Ваша заявка принята. Средства будут зарезервированы для стратегии.",
                {},
              );
              navigate(PATH_LIST.home);
            } else {
              toast.success("Success");
              navigate(PATH_LIST.strategy(asset_id!));
            }
          }
        },
      );
    }
  };

  const isSPY = assetInfo?.asset?.symbol === "SPDR";
  const isUATE = assetInfo?.asset?.symbol === "UATE_CN";
  const [uateStep, setUateStep] = useState<number>(1);

  const isWrongAmountStep = useMemo(() => {
    return amount > 0 && step && amount % step !== 0;
  }, [amount, step]);

  const unavailable = useCallback(() => {
    const totalAmount = parseFloat(getTotalPriceAndCommission());
    const userBalance = getUserAccount().amount as number;
    const [min, max] = getAmountRange();

    if (isWrongAmountStep) return true;

    if (operation === OperationType.Buy) {
      if (!amount || amount < 0 || !userBalance || !totalAmount || amount < min)
        return true;

      if (isSPY) {
        const maxSPYAmount = 1000;
        const [spyCommission] = getCommissionWrap();
        const maxAllowedTotal =
          maxSPYAmount + (spyCommission * maxSPYAmount) / 100;

        if (totalAmount > maxAllowedTotal) {
          return true;
        }
      }

      if (isUATE) {
        const [min, max] = getAmountRange();
        if (totalAmount > max) {
          return true;
        }
        if (totalAmount < min) {
          return true;
        }
        return false;
      }

      return totalAmount > userBalance;
    } else {
      if (!amount || amount < 0 || !totalAmount || amount < min) return true;
      return amount > max;
    }
  }, [
    amount,
    getTotalPriceAndCommission,
    getUserAccount,
    getAmountRange,
    operation,
    isSPY,
    assetInfo,
    isWrongAmountStep,
  ]);

  const UATEBanner = useLocalizedImage({
    am: UATEBannerAM,
    ru: UATEBannerRU,
    en: UATEBannerEN,
  });

  const strategy = assetInfo;
  const customInfo = strategy?.customInfo || null;

  const handleUateBuy = () => {
    if (uateStep === 1) {
      setUateStep(2);
    } else if (uateStep === 2) {
      setUateStep(3);
    } else {
      action(() => setConfirmDeal(true));
    }
  };

  if (isLoading) return <Loading />;

  if (isUATE) {
    return (
      <div
        className={`deal-container strategy-invest-deal-container uate-deal-container ${
          uateStep === 2 || uateStep === 3 ? "uate-step-2-container" : ""
        }`}
      >
        <div className="deal-header">
          <img
            className="go-back-portfolio"
            onClick={() => {
              if (uateStep === 1) {
                navigate(PATH_LIST.home);
              } else {
                setUateStep(uateStep - 1);
              }
            }}
            src={GoBack}
            alt="Go Back"
          />
          <div>
            <p>{i("tradingTerminal")}</p>
          </div>

          {(uateStep === 2 || uateStep === 3) && (
            <a
              className="uate-document-download-btn"
              href={
                contractType && uateStep === 3
                  ? getPreviewIccContractUrl(
                      user!.id,
                      entity,
                      (contractAmountType === "shares"
                        ? getTotalQuantity()
                        : amount) || 0,
                      contractType,
                    )
                  : i18n.language === "am"
                  ? DocumentAM
                  : DocumentEN
              }
              target="_blank"
              rel="noreferrer"
            >
              <img src={DocumentDownloadIcon} alt="DocumentDownloadIcon" />
            </a>
          )}
        </div>

        {uateStep === 1 && (
          <div className="uate-deal-container_uate-banner">
            <img src={UATEBanner} alt="Banner" />
          </div>
        )}

        <div
          className={
            uateStep === 2 || uateStep === 3
              ? "deal-main uate-document-wrapper"
              : "deal-main"
          }
        >
          <div className="uate-deal-step">
            {/* <h4>
              {u("stepLabel")} {uateStep}
            </h4> */}
            <p>
              {uateStep === 1
                ? t("enterAmountToInsvest")
                : uateStep === 2
                ? u("reviewAgreement")
                : u("pleaseReviewAndSignAnnex1")}
            </p>
          </div>

          {uateStep === 1 && (
            <>
              <div className="deal-on-account spy-deal-on-account">
                <span>{t("mainAccount")}</span>
                <p>
                  {getUserAccount().amount}
                  {currSymbol}

                  <img
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(PATH_LIST.deposit, {
                        state: { currency: "USD" },
                      });
                    }}
                    src={TopUp}
                    alt="TopUp"
                  />
                </p>
              </div>

              {getAmountRange()[0] > 0 &&
                getUserAccount().amount < getAmountRange()[0] && (
                  <div className="bargaining-alert">
                    <span>{t("insufficientFunds")}</span>
                  </div>
                )}

              <div className="price-box-uate">
                <input
                  id="amount"
                  className="specific-price-input"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    const strValue = String(e.target.value);
                    const value = +strValue || "";
                    setAmount(value as number);
                  }}
                  min={getAmountRange()[0]}
                  max={getAmountRange()[1]}
                  step={step}
                  autoFocus={true}
                />
                <span>{currSymbol}</span>
              </div>

              {isWrongAmountStep && (
                <span
                  className="quantity-validation-alert"
                  dangerouslySetInnerHTML={{
                    __html: t("purchaseInMultiplesOf1000Only", { step }),
                  }}
                />
              )}

              <div className="price-box-uate-info">
                <p>
                  {t("min")}:{" "}
                  <span>
                    {getAmountRange()[0] === 10000
                      ? "10,000 " + currSymbol
                      : getAmountRange()[0] + currSymbol}
                  </span>
                </p>
                <p>
                  {t("commission")}:{" "}
                  <span>
                    {getCommissionWrap()[2]} {currSymbol}
                  </span>
                </p>
              </div>

              {alertText && (
                <div className="bargaining-alert info">
                  <span
                    style={{ fontSize: "12px" }}
                    dangerouslySetInnerHTML={{
                      __html: getMLValue(alertText, i18n.language),
                    }}
                  ></span>
                </div>
              )}
            </>
          )}

          {uateStep === 2 && (
            <div className="uate-document uate-document-step-2">
              <PDF uri={i18n.language === "am" ? DocumentAM : DocumentEN} />
            </div>
          )}

          {contractType && uateStep === 3 && (
            <div className="uate-document">
              <PDF
                uri={getPreviewIccContractUrl(
                  user!.id,
                  entity,
                  (contractAmountType === "shares"
                    ? getTotalQuantity()
                    : amount) || 0,
                  contractType,
                )}
              />
            </div>
          )}
        </div>

        <div className="btn-wrapper deal-btn-wrapper">
          {uateStep === 2 && (
            <p className="uate-terms-submit-text">{u("acceptTermsOnSubmit")}</p>
          )}

          <Button
            type="submit"
            disabled={unavailable() || !canTrade || isLoading}
            variant="pink"
            className="customBtn deal-btn"
            onClick={handleUateBuy}
          >
            {uateStep === 1
              ? t("continue")
              : uateStep === 2
              ? u("submitOrder")
              : t("sign")}
          </Button>
        </div>

        {confirmDeal === true && (
          <Modal
            customModal="modalLogOut"
            onClick={handleBuy}
            onHide={() => setConfirmDeal(false)}
            title={t("confirmDeal")}
            text={t("toCompleteDeal")}
            btnText={t("confirm")}
            showSecondBtn={false}
            customOutlet="customOutlet"
            confirmDeal={true}
            isLoading={load}
            dealError={dealError}
          />
        )}
      </div>
    );
  }

  return (
    <div className="deal-container strategy-invest-deal-container">
      <div
        className="deal-header"
        style={{ marginBottom: isSPY ? "0px" : "12px" }}
      >
        <img
          className="go-back-portfolio"
          onClick={() => navigate(-1)}
          src={GoBack}
          alt="Go Back"
        />
        <p>{isSPY ? i("tradingTerminal") : t("invest")}</p>
      </div>

      {isSPY && (
        <ChartViewCustom assetId={customInfo!.info!.invest_chart_id!} />
      )}

      <div className="deal-main">
        {!isSPY ? (
          <div className="deal-on-account-strategy">
            <span className="title-span">{t("account")}</span>
            <div className="deal-on-account-box">
              <div>
                <span>{t("mainAccount")}</span>
                <p>
                  {getUserAccount().amount}
                  {currSymbol}
                </p>
              </div>
              {showOtherAccounts && (
                <div
                  className="arrow-down"
                  onClick={() => {
                    setAccountsModal(true);
                  }}
                >
                  <img src={ChevronDown} alt="arrow-down" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="deal-on-account spy-deal-on-account">
            <span>{t("mainAccount")}</span>
            <p>
              {getUserAccount().amount}
              {currSymbol}

              <img
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(PATH_LIST.deposit, {
                    state: { currency: "USD" },
                  });
                }}
                src={TopUp}
                alt="TopUp"
              />
            </p>
          </div>
        )}

        {isOptionIndex && operation === OperationType.Buy ? (
          <>
            <div style={{ marginTop: "14px" }}>
              {indexList?.length ? (
                <Currency
                  image={selectedIndexAsset?.asset?.image || ""}
                  onClick={() => setIndexModal("custom")}
                  custom={selectedIndex}
                  customText={t("")}
                />
              ) : (
                <div className="bargaining-alert">
                  <span>{t("haveNoInvestments")}</span>
                </div>
              )}
            </div>

            <div className="current-investment-amount">
              <label>
                {t("currentInvestmentAmount")}{" "}
                {selectedIndexAsset?.asset?.name || ""}
              </label>
              <div>
                <p>
                  {getIndexInvestments()} {currSymbol}
                </p>
              </div>
            </div>

            {contractType && (
              <TermsCheckbox
                className="custom-strategy-checkbox"
                term={t("iConfirmThat")}
                CustomName={t("document")}
                CustomDoc={getPreviewIccContractUrl(
                  user!.id,
                  entity,
                  amount || 0,
                  contractType,
                )}
                htmlForId="1"
                isChecked={checkedContract}
                onChange={() => setCheckedContract(!checkedContract)}
              />
            )}

            <TermsCheckbox
              className="capital-protection-checkbox"
              term={t("capitalProtectionText")}
              disabled={!selectedIndexAsset || !selectedIndexAsset?.quantity}
              onClick={() =>
                navigate(PATH_LIST.strategy(asset_id), {
                  state: { tab: "capitalProtection" },
                })
              }
              onClickText={`(${t("description").toLowerCase()})`}
              htmlForId="2"
              isChecked={isProtected}
              onChange={() => {
                setIsProtected(!isProtected);
              }}
            />

            {isProtected && (
              <>
                <div className="deal-commission">
                  <p>
                    {t("capitalProtectionAmount", { cap: capitalProtection })}:{" "}
                    <b>
                      {amount}
                      {currSymbol}
                    </b>
                  </p>
                </div>

                {getAssetCommission()}
              </>
            )}
          </>
        ) : (
          <>
            {asset && asset.quantity > 0 && (
              <div className="price-box price-box-invest">
                <p>{t("currentInvestmentAmount")}</p>
                <div>
                  <p>
                    {getTotalAmount()} {currSymbol}
                  </p>
                  {/* <span>
                {annualYield}% {t("annualReturn")}
              </span> */}
                </div>
              </div>
            )}

            {getAmountRange()[0] > 0 &&
            getUserAccount().amount < getAmountRange()[0] ? (
              <BargainingAlert variant="danger" text={t("insufficientFunds")} />
            ) : (
              <></>
            )}

            <div
              className="price-box price-box-invest"
              style={{ marginBottom: isWrongAmountStep ? "22px" : "0px" }}
            >
              <p>
                {operation === OperationType.Buy
                  ? t("enterAmountToInsvest")
                  : t("enterNewAmount")}
              </p>

              <div>
                <input
                  id="amount"
                  className="specific-price-input"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    // const newValue = e.target.value.replace(/[^0-9.]/g, "");
                    // setSpecificPrice(parseFloat(newValue));
                    const strValue = String(e.target.value);
                    const value = +strValue || "";
                    setAmount(value as number);
                  }}
                  min={getAmountRange()[0]}
                  max={getAmountRange()[1]}
                  step={step}
                  autoFocus={true}
                />
                <span>{currSymbol}</span>
              </div>

              {isWrongAmountStep && (
                <span
                  className="quantity-validation-alert"
                  dangerouslySetInnerHTML={{
                    __html: t("purchaseInMultiplesOf1000Only", { step }),
                  }}
                />
              )}
            </div>

            <div className="price-box-uate-info">
              {operation === OperationType.Buy && getAmountRange()[0] > 0 && (
                <p>
                  {t("min")}:{" "}
                  <span>
                    {getAmountRange()[0]}
                    {currSymbol}
                  </span>
                </p>
              )}

              {getAmountRange()[0] > 0 &&
                getAmountRange()[1] > getAmountRange()[0] && (
                <p>
                  {t("max")}:{" "}
                  <span>
                    {getAmountRange()[1]}
                    {currSymbol}
                  </span>
                </p>
              )}

              {getAssetCommission()}
            </div>

            {alertText && (
              <div className="bargaining-alert info">
                <span
                  style={{ fontSize: "12px" }}
                  dangerouslySetInnerHTML={{
                    __html: getMLValue(alertText, i18n.language),
                  }}
                ></span>
              </div>
            )}

            {!isSPY &&
              agreements.map((agreement, index) => (
                <div style={{ marginTop: "10px" }} key={index}>
                  <TermsCheckbox
                    term={getMLValue(agreement.label, i18n.language)}
                    customText
                    htmlText={
                      agreement.text
                        ? getMLValue(agreement.text, i18n.language)
                        : undefined
                    }
                    htmlForId={`agreement-${index}`}
                    isChecked={checked[index]}
                    onChange={() => {
                      const newChecked = [...checked];
                      newChecked[index] = !newChecked[index];
                      setChecked(newChecked);
                    }}
                    // smallFont
                  />
                </div>
              ))}

            {contractType && operation === OperationType.Buy && (
              <TermsCheckbox
                className="custom-strategy-checkbox"
                term={t("iConfirmThat")}
                CustomName={t("investOrder")}
                CustomDoc={getPreviewIccContractUrl(
                  user!.id,
                  entity,
                  (contractAmountType === "shares"
                    ? getTotalQuantity()
                    : amount) || 0,
                  contractType,
                )}
                htmlForId="1"
                isChecked={checkedContract}
                onChange={() => setCheckedContract(!checkedContract)}
              />
            )}
            {capitalProtection && operation === OperationType.Buy && (
              <TermsCheckbox
                className="capital-protection-checkbox"
                term={t("capitalProtectionText")}
                onClick={() =>
                  navigate(PATH_LIST.strategy(asset_id), {
                    state: { tab: "capitalProtection" },
                  })
                }
                onClickText={`(${t("description").toLowerCase()})`}
                htmlForId="2"
                isChecked={isProtected}
                onChange={() => setIsProtected(!isProtected)}
              />
            )}

            {isProtected && (
              <div className="deal-commission">
                <p>
                  {t("capitalProtectionAmount", { cap: capitalProtection })}:{" "}
                  <b>
                    {amount / 10}
                    {currSymbol}
                  </b>
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={`btn-wrapper deal-btn-wrapper ${
          isSPY ? "spy-btn-wrapper" : ""
        }`}
      >
        {isSPY &&
          agreements.map((agreement, index) => (
            <div
              className="spy-agreement-checkbox"
              style={{ marginTop: "10px" }}
              key={index}
            >
              <TermsCheckbox
                term={getMLValue(agreement.label, i18n.language)}
                customText
                htmlText={
                  agreement.text
                    ? getMLValue(agreement.text, i18n.language)
                    : undefined
                }
                htmlForId={`agreement-${index}`}
                isChecked={checked[index]}
                onChange={() => {
                  const newChecked = [...checked];
                  newChecked[index] = !newChecked[index];
                  setChecked(newChecked);
                }}
                smallFont
              />
            </div>
          ))}

        <Button
          type="submit"
          disabled={
            unavailable() ||
            !canTrade ||
            isLoading ||
            (operation === OperationType.Buy &&
              contractType &&
              !checkedContract) ||
            (checked.length > 0 && !checked.every((v) => v))
          }
          variant="dark"
          className="customBtn deal-btn"
          onClick={() => action(() => setConfirmDeal(true))}
        >
          {operation === OperationType.Buy
            ? t("confirmInvestment")
            : t("confirm")}
          <span>{getTotalPriceAndCommission()}</span>
        </Button>
      </div>

      {confirmDeal === true && (
        <Modal
          customModal="modalLogOut"
          onClick={handleBuy}
          onHide={() => setConfirmDeal(false)}
          title={t("confirmDeal")}
          text={t("toCompleteDeal")}
          btnText={t("confirm")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal={true}
          isLoading={load}
          dealError={dealError}
        />
      )}

      {indexModal && (
        <CurrencyModal
          type={indexModal}
          onClose={() => setIndexModal(null)}
          onCurrencyChange={(c) => setSelectedIndex(c)}
          selectedCustom={selectedIndex}
          customList={
            indexList?.map((ua) => ({
              id: ua.asset.symbol,
              name: ua.asset.name,
              image: ua.asset.image,
            })) || []
          }
          customText="Select INDEX"
          selectedCurrency={""}
        />
      )}

      {accountsModal && (
        <div
          className="currency-card-bottomsheet-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setAccountsModal(false);
          }}
        >
          <div
            className="currency-card-bottomsheet"
            onClick={(e) => {
              e.stopPropagation();
              timer(0.1).then(() => {
                setAccountsModal(false);
              });
            }}
          >
            <div className="currency-card-bottomsheet__header">
              <p>{t("chooseCurrency")}</p>
              <button
                onClick={() => setAccountsModal(false)}
                aria-label={t("close")}
              >
                <img src={Close} alt={t("close")} />
              </button>
            </div>

            <div className="currency-card-bottomsheet__rows">
              {user_deposit_accounts
                .filter(
                  (item) =>
                    item.currency.code === baseCurrency ||
                    currencies.map((c) => c.code).includes(item.currency.code),
                )
                .map((item) => (
                  <div
                    key={item.currency.code}
                    className={`currency-card-bottomsheet__rows__row ${
                      item.currency.code === selectedCurrency ? "selected" : ""
                    }`}
                    onClick={() => {
                      setAmount(0);
                      setSelectedCurrency(item.currency.code);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setAmount(0);
                        setSelectedCurrency(item.currency.code);
                      }
                    }}
                  >
                    <div className="currency-card-bottomsheet__rows__row__left">
                      <div className="cur-flag-wrapper">
                        <img
                          className="checked"
                          src={CURRENCY_FLAGS[item.currency.code]}
                          alt={`${item.currency.code} flag`}
                        />
                        {item.currency.code === selectedCurrency && (
                          <img
                            className="cur-flag-checkmark"
                            src={CheckMark}
                            alt={t("selected")}
                          />
                        )}
                      </div>
                      <div>
                        <p>{item.currency.code}</p>
                        {/* <span>{item.currency.name}</span> */}
                      </div>
                    </div>
                    {/* !== 0: минусовой остаток тоже печатаем, см. home/index.tsx */}
                    {item.amount !== 0 && (
                      <p>
                        {+item.amount.toFixed(4)}
                        {item.currency.symbol}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
